import { NuxtI18nInstance } from "@nuxtjs/i18n";
import { DEFAULT_PAGINATOR_SIZES, INSTANCE_TYPE, WIDTH_NG } from "~/assets/js/constants";
import { createPaginator } from "~/assets/js/utils";
import {
  Action,
  Column,
  InstanceDTO,
  PaginatedResponse,
  TableHeader,
  TableOptions,
  TablePaginator,
} from "~/types/types";

export default class InstancesTransformer {
  static table(instances: PaginatedResponse<InstanceDTO>, options: TableOptions) {
    const { i18n } = options;

    const paginator = createPaginator<InstanceDTO>(instances, i18n, DEFAULT_PAGINATOR_SIZES);

    const headers: TableHeader[] = [
      {
        id: "name",
        label: i18n.t("instances.headers.name"),
        sortable: true,
      },
      {
        id: "server",
        label: i18n.t("instances.headers.server"),
        sortable: true,
      },
      {
        label: i18n.t("instances.headers.connectedOn"),
        id: "connectedOn",
        sortable: true,
      },
    ];

    return {
      paginator,
      sizes: [WIDTH_NG.MD, WIDTH_NG["3XL"], WIDTH_NG.LG],
      main: "name",
      headers,
      rows: [
        ...instances.collection
          .map((instance) => this.row(instance, { i18n }))
          .filter((instance) => instance.type === INSTANCE_TYPE.FEDERATED),
      ],
    };
  }

  static row(instance: InstanceDTO, { i18n }: { i18n: NuxtI18nInstance }) {
    const {
      id,
      name,
      server,
      createdOn,
      connectedOn,
      updatedOn,
      description,
      manageVaccines,
      type,
    } = instance;
    const actions: Action[] = [];

    const columns: Column[] = [
      {
        type: "text",
        id: "name",
        data: { label: name },
      },
      {
        type: "text",
        data: { label: server },
        id: "server",
      },
      {
        type: "text",
        data: {
          label: connectedOn ? i18n.d(new Date(connectedOn * 1000), "medium") : "",
        },
        id: "connectedOn",
      },
    ];

    return {
      id,
      name,
      server,
      createdOn,
      connectedOn,
      updatedOn,
      description,
      manageVaccines,
      type,
      uuid: instance.id,
      columns,
      actions,
    };
  }

  static extendedTable(instances: PaginatedResponse<InstanceDTO>, options: TableOptions) {
    const { total, page, size } = instances;
    const { i18n } = options;

    const paginator: TablePaginator = {
      total,
      page,
      size,
      language: i18n.locale,
    };

    const headers: TableHeader[] = [
      {
        label: i18n.t("instances.headers.name"),
        id: "name",
        sortable: true,
      },
      {
        label: i18n.t("instances.headers.description"),
        id: "description",
        sortable: true,
      },
      {
        label: i18n.t("instances.headers.server"),
        id: "server",
        sortable: true,
      },
      {
        label: i18n.t("instances.headers.apikey"),
        id: "id",
        sortable: true,
      },
      {
        label: i18n.t("instances.headers.connectedOn"),
        id: "connectedOn",
        sortable: true,
      },
    ];
    const sizes = [WIDTH_NG.MD, WIDTH_NG.LG, WIDTH_NG.LG, WIDTH_NG["2XL"], WIDTH_NG.LG];

    return {
      paginator,
      sizes,
      main: "name",
      headers,
      rows: [
        ...instances.collection
          .map((item) => this.extendedRow(item, { i18n }))
          .filter((item) => item.type === INSTANCE_TYPE.FEDERATED),
      ],
    };
  }

  static extendedRow(instance: InstanceDTO, { i18n }: { i18n: NuxtI18nInstance }) {
    const {
      id,
      name,
      server,
      createdOn,
      connectedOn,
      updatedOn,
      description,
      manageVaccines,
      type,
    } = instance;

    const actions: Action[] = [
      {
        active: false,
        id: "instance-view",
        icon: "s2-icon-eye-regular",
        label: i18n.t(`generic.open`),
        loading: false,
      },
    ];

    const columns: Column[] = [
      {
        type: "text",
        data: {
          label: name,
          icon: "mc-circle-nodes-solid",
        },
        id: "name",
      },
      {
        type: "text",
        data: {
          label: description,
        },
        id: "description",
      },
      {
        type: "text",
        data: {
          label: server,
        },
        id: "server",
      },
      {
        type: "text",
        data: {
          label: id,
        },
        id: "id",
      },
      {
        data: {
          label: connectedOn ? i18n.d(new Date(connectedOn * 1000), "medium") : "",
        },
        id: "connectedOn",
        type: "text",
      },
    ];

    return {
      id,
      name,
      server,
      createdOn,
      connectedOn,
      updatedOn,
      description,
      manageVaccines,
      type,
      uuid: instance.id,
      columns,
      actions,
    };
  }
}
